import { useEffect, useState } from "react";
import AdImage from "@/components/AdImage";
import api from "@/lib/api";

// Queue-screen sponsor banner. Content is managed from the Admin Panel (Ads → zone "queue").
// The whole space can be turned off from Admin → Placement Settings. Shows nothing when
// the zone is disabled or has no active campaigns (no demo/house ads).
export default function AdBanner({ compact = false }) {
  const [ads, setAds] = useState(null); // null = loading, [] = hidden

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/config/ad-settings").then((r) => r.data).catch(() => ({ queue: true })),
      api.get("/config/ads", { params: { zone: "queue" } }).then((r) => r.data).catch(() => []),
    ]).then(([settings, list]) => {
      if (!active) return;
      if (settings && settings.queue === false) { setAds([]); return; }
      const mapped = (list || []).map((a) => ({
        id: a.ad_id, title: a.title, subtitle: a.subtitle || "", tag: "SPONSOR", image: a.image_url, link: a.link,
      }));
      setAds(mapped);
    });
    return () => { active = false; };
  }, []);

  const [i, setI] = useState(0);
  useEffect(() => {
    if (!ads || ads.length < 2) return;
    const t = setInterval(() => setI((p) => (p + 1) % ads.length), 5000);
    return () => clearInterval(t);
  }, [ads]);

  if (ads === null || ads.length === 0) return null;

  const ad = ads[i % ads.length];
  const inner = (
    <div data-testid="ad-banner" className="relative overflow-hidden rounded-2xl glow-border grain" style={{ height: compact ? 96 : 128 }}>
      {ad.image && (
        <AdImage src={ad.image} alt={ad.title} className="absolute inset-0 w-full h-full object-cover rise" style={{ opacity: 0.35 }} />
      )}
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--surface) 10%, transparent 90%)" }} />
      <div className="relative z-10 h-full flex items-center justify-between px-6 gap-4">
        <div className="min-w-0">
          <span className="text-[10px] tracking-[0.3em] font-bold text-primary-c">{ad.tag} · SPONSORED</span>
          <h4 className="font-display font-bold text-lg sm:text-xl truncate" style={{ color: "var(--text)" }}>{ad.title}</h4>
          {ad.subtitle && <p className="text-xs sm:text-sm truncate" style={{ color: "var(--text-2)" }}>{ad.subtitle}</p>}
        </div>
        {ads.length > 1 && (
          <div className="flex gap-1.5 shrink-0">
            {ads.map((_, idx) => (
              <span key={idx} className="w-1.5 h-1.5 rounded-full transition-colors" style={{ background: idx === (i % ads.length) ? "var(--primary)" : "var(--border)" }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return ad.link ? <a href={ad.link} target="_blank" rel="noreferrer" className="block">{inner}</a> : inner;
}
